import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { LINE_ITEM_ITEM, LINE_ITEM_CART_ITEM } from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;
const ITEM_CODES = [LINE_ITEM_ITEM, LINE_ITEM_CART_ITEM];

/**
 * CL-CART-01 — the "Item subtotal" row shown on the checkout pages, in place of the template's
 * `LineItemBasePriceMaybe`.
 *
 * The template labels that row from `OrderBreakdown.baseUnitQuantity`, which reads "Unit price"
 * for one item and "$450.00 x 2" for more. Both are right on an order page, where nothing else
 * itemises the purchase. On a checkout panel the item list directly above already says
 * "Qty 2 · $450.00 each", so the money rows only need the sum — and it has to be worded exactly
 * as CartCheckoutPage words it, or the two checkouts disagree about the same number.
 *
 * Uses `CartOrderSummary.itemSubtotal`, the basket checkout's own key, deliberately: an operator
 * editing that microcopy in Console changes both checkouts at once.
 *
 * Sums `lineTotal`, so quantity is already accounted for, and covers `line-item/cart-item` too —
 * a basket's non-anchor items would otherwise be missing from a row that claims to be a subtotal.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.lineItems - line items already filtered for this user role
 * @param {intlShape} props.intl
 * @returns {JSX.Element|null} null when there is nothing to sum
 */
const LineItemCheckoutItemSubtotal = props => {
  const { lineItems, intl } = props;

  const itemLines = (lineItems || []).filter(
    line => ITEM_CODES.includes(line.code) && !line.reversal
  );
  if (itemLines.length === 0) {
    return null;
  }

  const currency = itemLines[0].lineTotal?.currency;
  const amount = itemLines.reduce((sum, line) => sum + (line.lineTotal?.amount || 0), 0);
  if (!currency) {
    return null;
  }

  return (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="CartOrderSummary.itemSubtotal" />
      </span>
      <span className={css.itemValue}>{formatMoney(intl, new Money(amount, currency))}</span>
    </div>
  );
};

export default LineItemCheckoutItemSubtotal;
